import { useEffect } from 'react';

export const SITE_URL = 'https://spreewald-lift.de';
export const SITE_NAME = 'Spreewald Lift';
export const DEFAULT_IMAGE = `${SITE_URL}/images/hero/product.png`;

const defaultTitle = 'Spreewald Lift | Arbeitsbuehnen mieten im Spreewald';
const defaultDescription = 'Arbeitsbuehne mieten im Spreewald: Spreewald Lift vermietet moderne Arbeitsbuehnen fuer Baumpflege, Fassadenarbeiten, Dacharbeiten und Montagearbeiten.';

export function absoluteUrl(path = '/') {
  if (!path) return SITE_URL;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

export function localBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#business`,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/images/logos/spreewald-logo.svg`,
    image: DEFAULT_IMAGE,
    description: 'Arbeitsbuehnenverleih im Spreewald und Umgebung fuer Baumpflege, Fassadenarbeiten, Dacharbeiten und Montagearbeiten.',
    email: 'info@spreewald-lift.de',
    priceRange: 'EUR',
    areaServed: ['Spreewald', 'Luebbenau', 'Luebben', 'Cottbus', 'Berlin', 'Brandenburg'],
    knowsAbout: ['Arbeitsbuehne mieten Spreewald', 'Hebebuehne mieten', 'Arbeitsbuehnenverleih', 'Baumpflege', 'Fassadenarbeiten', 'Dacharbeiten', 'Montagearbeiten'],
    makesOffer: {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: 'Arbeitsbuehnenverleih',
        serviceType: 'Arbeitsbuehne mieten',
        areaServed: 'Spreewald und Umgebung'
      }
    }
  };
}

export function serviceSchema({ name = 'Arbeitsbuehne mieten', description = defaultDescription, path = '/' } = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${absoluteUrl(path)}#service`,
    name,
    serviceType: 'Arbeitsbuehnenverleih',
    provider: { '@id': `${SITE_URL}/#business` },
    areaServed: ['Spreewald', 'Luebbenau', 'Luebben', 'Cottbus', 'Berlin'],
    description
  };
}

export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path)
    }))
  };
}

function ensureMeta(selector, attrs) {
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    const name = selector.match(/meta\[name="([^"]+)"\]/)?.[1];
    const property = selector.match(/meta\[property="([^"]+)"\]/)?.[1];
    if (name) element.setAttribute('name', name);
    if (property) element.setAttribute('property', property);
    document.head.appendChild(element);
  }
  Object.entries(attrs).forEach(([key, value]) => element.setAttribute(key, value));
}

function ensureLink(selector, attrs) {
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement('link');
    const rel = selector.match(/link\[rel="([^"]+)"\]/)?.[1];
    if (rel) element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  Object.entries(attrs).forEach(([key, value]) => element.setAttribute(key, value));
}

function setJsonLd(id, data) {
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.id = id;
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

export function useSeo({
  title = defaultTitle,
  description = defaultDescription,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  robots = 'index, follow, max-image-preview:large',
  jsonLd = []
} = {}) {
  useEffect(() => {
    const canonical = absoluteUrl(path);
    const ogImage = absoluteUrl(image);

    document.documentElement.lang = 'de';
    document.title = title;
    ensureMeta('meta[name="description"]', { content: description });
    ensureMeta('meta[name="robots"]', { content: robots });
    ensureMeta('meta[name="author"]', { content: SITE_NAME });
    ensureMeta('meta[property="og:type"]', { content: type });
    ensureMeta('meta[property="og:locale"]', { content: 'de_DE' });
    ensureMeta('meta[property="og:site_name"]', { content: SITE_NAME });
    ensureMeta('meta[property="og:title"]', { content: title });
    ensureMeta('meta[property="og:description"]', { content: description });
    ensureMeta('meta[property="og:url"]', { content: canonical });
    ensureMeta('meta[property="og:image"]', { content: ogImage });
    ensureMeta('meta[property="og:image:alt"]', { content: 'Spreewald Lift Arbeitsbuehne Octoplus 21' });
    ensureMeta('meta[name="twitter:card"]', { content: 'summary_large_image' });
    ensureMeta('meta[name="twitter:title"]', { content: title });
    ensureMeta('meta[name="twitter:description"]', { content: description });
    ensureMeta('meta[name="twitter:image"]', { content: ogImage });
    ensureLink('link[rel="canonical"]', { href: canonical });

    setJsonLd('structured-data-local-business', localBusinessSchema());
    jsonLd.forEach((item, index) => setJsonLd(`structured-data-page-${index}`, item));

    let staleIndex = jsonLd.length;
    let stale = document.getElementById(`structured-data-page-${staleIndex}`);
    while (stale) {
      stale.remove();
      staleIndex += 1;
      stale = document.getElementById(`structured-data-page-${staleIndex}`);
    }
  }, [title, description, path, image, type, robots, JSON.stringify(jsonLd)]);
}
